const DB_NAME = 'atone-offline';
const DB_VERSION = 1;
const STORE_NAME = 'pending-logs';

function openOfflineDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// Store logData in the queue, replacing any existing entry for the same set
// so we don't accumulate stale duplicates when the user edits a set while offline.
export async function queueLog(logData) {
  const db = await openOfflineDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const setKey = `${logData.session_id}:${logData.program_exercise_id}:${logData.set_number}`;

    const getAllReq = store.getAll();
    getAllReq.onsuccess = () => {
      const all = getAllReq.result;
      const existing = all.find(item => item.setKey === setKey);
      if (existing) {
        // Overwrite with latest data — keeps queue lean
        const putReq = store.put({ ...existing, logData, queuedAt: Date.now() });
        putReq.onsuccess = () => resolve(putReq.result);
        putReq.onerror = () => reject(putReq.error);
      } else {
        const addReq = store.add({ setKey, logData, queuedAt: Date.now() });
        addReq.onsuccess = () => resolve(addReq.result);
        addReq.onerror = () => reject(addReq.error);
      }
    };
    getAllReq.onerror = () => reject(getAllReq.error);
  });
}

export async function getPendingLogs() {
  const db = await openOfflineDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function removePendingLog(id) {
  const db = await openOfflineDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

// ── Session completion queue ─────────────────────────────────
// Stored in the same object store with type: 'session_complete'
// so a single sync pass can drain everything.

export async function queueSessionComplete(sessionId, updateData) {
  const db = await openOfflineDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const setKey = `session_complete:${sessionId}`;

    const getAllReq = store.getAll();
    getAllReq.onsuccess = () => {
      const all = getAllReq.result;
      const existing = all.find(item => item.setKey === setKey);
      if (existing) {
        const putReq = store.put({ ...existing, updateData, queuedAt: Date.now() });
        putReq.onsuccess = () => resolve(putReq.result);
        putReq.onerror = () => reject(putReq.error);
      } else {
        const addReq = store.add({ setKey, type: 'session_complete', sessionId, updateData, queuedAt: Date.now() });
        addReq.onsuccess = () => resolve(addReq.result);
        addReq.onerror = () => reject(addReq.error);
      }
    };
    getAllReq.onerror = () => reject(getAllReq.error);
  });
}

export async function getPendingSessionCompletions() {
  const all = await getPendingLogs(); // reads the whole store
  return all.filter(item => item.type === 'session_complete');
}

export async function removePendingSessionCompletion(id) {
  return removePendingLog(id); // same delete logic
}

// Replace a temporary offline session ID with the real DB session ID in all queued logs.
// Called during sync reconciliation when a session was created offline.
export async function updatePendingLogSessionIds(tempId, realId) {
  const db = await openOfflineDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAll();
    req.onsuccess = () => {
      const toUpdate = req.result.filter(item => item.logData?.session_id === tempId);
      if (toUpdate.length === 0) { resolve(); return; }
      let remaining = toUpdate.length;
      for (const item of toUpdate) {
        // The set key carries the session id too. Left on the temporary one, a later edit of the
        // same set (queued under the real id) would be added beside this entry instead of replacing it.
        const putReq = store.put({
          ...item,
          setKey: `${realId}:${item.logData.program_exercise_id}:${item.logData.set_number}`,
          logData: { ...item.logData, session_id: realId },
        });
        // eslint-disable-next-line no-loop-func
        putReq.onsuccess = () => { if (--remaining === 0) resolve(); };
        putReq.onerror = () => reject(putReq.error);
      }
    };
    req.onerror = () => reject(req.error);
  });
}
